import { useState, useEffect, useCallback } from 'react'
import type { AddressResult } from '../types'
import { reverseGeocode } from '../services/api'
import { getSavedAddresses, getRecentAddresses, addRecentAddress, saveHomeAddress, saveWorkAddress } from '../utils/storage'

// Иконки для разных типов мест
const LoadingSpinner = () => (
  <svg className="animate-spin h-5 w-5 text-accent-green" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
)

const LocationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-accent-green">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
)

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-primary">
    <path d="M3 12L12 3L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 10V20C5 20.5523 5.44772 21 6 21H18C18.5523 21 19 20.5523 19 20V10" stroke="currentColor" strokeWidth="2" />
    <path d="M9 21V14H15V21" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const WorkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-secondary">
    <rect x="3" y="7" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M8 7V5C8 3.89543 8.89543 3 10 3H14C15.1046 3 16 3.89543 16 5V7" stroke="currentColor" strokeWidth="2" />
    <path d="M12 12V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const RecentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-text-muted">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const PlaceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-text-secondary">
    <path d="M12 21C12 21 5 13.5 5 9C5 5.13401 8.13401 2 12 2C15.866 2 19 5.13401 19 9C19 13.5 12 21 12 21Z" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
)

const MapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-primary-light">
    <path d="M9 3L3 6V21L9 18M9 3L15 6M9 3V18M15 6L21 3V18L15 21M15 6V21M9 18L15 21" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
)

// Demo данные
const DEMO_ADDRESSES: AddressResult[] = [
  { display_name: 'Красная площадь, Москва, Россия', lat: 55.7539, lon: 37.6208 },
  { display_name: 'Тверская улица, 1, Москва, Россия', lat: 55.7578, lon: 37.6137 },
  { display_name: 'Арбат, Москва, Россия', lat: 55.7520, lon: 37.5917 },
  { display_name: 'Парк Горького, Москва, Россия', lat: 55.7312, lon: 37.6015 },
  { display_name: 'ВДНХ, Москва, Россия', lat: 55.8267, lon: 37.6378 },
  { display_name: 'Москва-Сити, Москва, Россия', lat: 55.7497, lon: 37.5393 },
  { display_name: 'Большой театр, Москва, Россия', lat: 55.7601, lon: 37.6186 },
  { display_name: 'Третьяковская галерея, Москва, Россия', lat: 55.7415, lon: 37.6208 },
]

// Default addresses (used when localStorage is empty)
const DEFAULT_SAVED_ADDRESSES = {
  home: { display_name: 'Ленинградский проспект, 29к1, Москва', lat: 55.7894, lon: 37.5249 },
  work: { display_name: 'Миусская площадь, 9с12, Москва', lat: 55.7723, lon: 37.5942 },
}

interface AddressSearchScreenProps {
  type: 'from' | 'to'
  onSelect: (address: AddressResult) => void
  onBack: () => void
}

export const AddressSearchScreen = ({ type, onSelect, onBack }: AddressSearchScreenProps) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AddressResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [savedAddresses, setSavedAddresses] = useState(getSavedAddresses())
  const [recentAddresses, setRecentAddresses] = useState(getRecentAddresses())
  const [editMode, setEditMode] = useState<'home' | 'work' | null>(null)

  const title = type === 'from' ? 'Точка посадки' : 'Пункт назначения'
  const placeholder = type === 'from' ? 'Откуда забрать?' : 'Куда едем?'

  // Handle address selection with recent addresses tracking
  const handleAddressSelect = (address: AddressResult) => {
    // If in edit mode, save to home/work
    if (editMode === 'home') {
      saveHomeAddress(address)
      setSavedAddresses(prev => ({ ...prev, home: address }))
      setEditMode(null)
      return
    }
    if (editMode === 'work') {
      saveWorkAddress(address)
      setSavedAddresses(prev => ({ ...prev, work: address }))
      setEditMode(null)
      return
    }

    // Add to recent addresses
    addRecentAddress(address)
    setRecentAddresses(getRecentAddresses())
    
    // Select address
    onSelect(address)
  }

  // Save current location as home/work
  const saveLocationAs = async (addressType: 'home' | 'work') => {
    setEditMode(addressType)
  }

  // Real GPS location detection
  const handleUseCurrentLocation = useCallback(async () => {
    setIsLoadingLocation(true)
    setLocationError(null)

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setLocationError('Геолокация не поддерживается вашим браузером')
      setIsLoadingLocation(false)
      return
    }

    // Request permission and get position
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 60000,
          }
        )
      })

      const { latitude: lat, longitude: lon } = position.coords
      
      // Reverse geocode to get address
      try {
        const address = await reverseGeocode(lat, lon)
        onSelect(address)
      } catch (geoError) {
        // Fallback: use coordinates without address name
        console.error('Reverse geocoding failed:', geoError)
        onSelect({
          display_name: `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
          lat,
          lon,
        })
      }
    } catch (error: any) {
      console.error('Geolocation error:', error)
      
      let errorMessage = 'Не удалось определить местоположение'
      
      if (error.code === 1) {
        errorMessage = 'Доступ к геолокации запрещён. Разрешите доступ в настройках браузера.'
      } else if (error.code === 2) {
        errorMessage = 'Местоположение недоступно. Проверьте, включён ли GPS.'
      } else if (error.code === 3) {
        errorMessage = 'Превышено время ожидания. Попробуйте ещё раз.'
      }
      
      setLocationError(errorMessage)
    } finally {
      setIsLoadingLocation(false)
    }
  }, [onSelect])

  // Search addresses
  const searchAddresses = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([])
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=6&countrycodes=ru&accept-language=ru`,
        { headers: { 'User-Agent': 'PrimeWallet-Taxi/1.0' } }
      )
      
      if (response.ok) {
        const data = await response.json()
        const addresses: AddressResult[] = data.map((item: any) => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          address: item.address,
        }))
        setResults(addresses)
        setIsLoading(false)
        return
      }
    } catch (e) {
      console.log('Nominatim failed, using demo data')
    }

    // Fallback to demo data
    const filtered = DEMO_ADDRESSES.filter(addr =>
      addr.display_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    setResults(filtered.length > 0 ? filtered : DEMO_ADDRESSES.slice(0, 4))
    setIsLoading(false)
  }, [])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchAddresses(query)
    }, 300)
    return () => clearTimeout(timer)
  }, [query, searchAddresses])

  return (
    <div className="h-full flex flex-col bg-dark animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/20 to-secondary/10 px-4 py-4 flex items-center gap-4">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-dark-secondary/80 backdrop-blur flex items-center justify-center hover:bg-primary/20 active:scale-95 transition-all"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <div className="flex-1">
          <p className="text-xs text-text-muted">{title}</p>
          <p className="text-white font-medium">{query || placeholder}</p>
        </div>
        <button className="px-3 py-1.5 rounded-lg bg-dark-secondary/80 text-text-secondary text-sm hover:bg-primary/20 transition-colors">
          Карта
        </button>
      </div>

      {/* Search input */}
      <div className="p-4 bg-dark-tertiary border-b border-primary/10">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            autoFocus
            className="w-full px-4 py-3.5 pl-12 bg-dark-secondary rounded-xl text-text-primary placeholder-text-muted border border-primary/10 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 outline-none transition-all text-base"
          />
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            {type === 'from' ? (
              <div className="w-3 h-3 rounded-full bg-accent-green shadow-sm shadow-accent-green/50" />
            ) : (
              <div className="w-3 h-3 rounded-full bg-accent-red shadow-sm shadow-accent-red/50" />
            )}
          </div>
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-text-muted/20 flex items-center justify-center hover:bg-text-muted/40 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto pb-safe">
        {/* Геолокация (только для "Откуда") */}
        {type === 'from' && !query && (
          <div className="border-b border-primary/10">
            <button
              onClick={handleUseCurrentLocation}
              disabled={isLoadingLocation}
              className="w-full flex items-center gap-4 px-4 py-4 hover:bg-primary/5 active:bg-primary/10 transition-colors disabled:opacity-60"
            >
              <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                {isLoadingLocation ? <LoadingSpinner /> : <LocationIcon />}
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium">
                  {isLoadingLocation ? 'Определяем...' : 'Ваше местоположение'}
                </p>
                <p className="text-text-muted text-sm">
                  {isLoadingLocation ? 'Пожалуйста, подождите' : 'Определить по GPS'}
                </p>
              </div>
            </button>
            {locationError && (
              <div className="px-4 pb-3">
                <p className="text-accent-red text-sm bg-accent-red/10 rounded-lg p-2">{locationError}</p>
              </div>
            )}
          </div>
        )}

        {/* Edit mode banner */}
        {editMode && (
          <div className="px-4 py-3 bg-primary/20 border-b border-primary/30">
            <p className="text-white text-sm font-medium">
              Выберите адрес для "{editMode === 'home' ? 'Дом' : 'Работа'}"
            </p>
            <button 
              onClick={() => setEditMode(null)}
              className="text-primary text-sm mt-1"
            >
              Отмена
            </button>
          </div>
        )}

        {/* Избранное (когда нет поиска) */}
        {!query && !editMode && (
          <div className="px-4 pt-4">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-3">Избранное</p>
            
            {/* Дом */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const addr = savedAddresses.home || DEFAULT_SAVED_ADDRESSES.home
                  handleAddressSelect(addr)
                }}
                className="flex-1 flex items-center gap-4 py-3 hover:bg-primary/5 active:bg-primary/10 transition-colors rounded-lg -mx-2 px-2"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <HomeIcon />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Дом</p>
                  <p className="text-text-muted text-sm truncate">
                    {(savedAddresses.home || DEFAULT_SAVED_ADDRESSES.home).display_name}
                  </p>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
              <button
                onClick={() => saveLocationAs('home')}
                className="px-3 py-2 text-xs text-primary hover:bg-primary/10 rounded-lg transition-colors"
              >
                Изменить
              </button>
            </div>

            {/* Работа */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const addr = savedAddresses.work || DEFAULT_SAVED_ADDRESSES.work
                  handleAddressSelect(addr)
                }}
                className="flex-1 flex items-center gap-4 py-3 hover:bg-primary/5 active:bg-primary/10 transition-colors rounded-lg -mx-2 px-2"
              >
                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                  <WorkIcon />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Работа</p>
                  <p className="text-text-muted text-sm truncate">
                    {(savedAddresses.work || DEFAULT_SAVED_ADDRESSES.work).display_name}
                  </p>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
              <button
                onClick={() => saveLocationAs('work')}
                className="px-3 py-2 text-xs text-primary hover:bg-primary/10 rounded-lg transition-colors"
              >
                Изменить
              </button>
            </div>
          </div>
        )}

        {/* Недавние */}
        {!query && !editMode && recentAddresses.length > 0 && (
          <div className="px-4 pt-4">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-3">Недавние</p>
            {recentAddresses.map((address, index) => (
              <AddressItem
                key={index}
                address={address}
                icon={<RecentIcon />}
                onSelect={handleAddressSelect}
              />
            ))}
          </div>
        )}

        {/* Популярные места (когда нет запроса) */}
        {!query && !editMode && (
          <div className="px-4 pt-4">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-3">Популярные места</p>
            {DEMO_ADDRESSES.slice(0, 5).map((address, index) => (
              <AddressItem
                key={index}
                address={address}
                icon={<PlaceIcon />}
                onSelect={handleAddressSelect}
              />
            ))}
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="py-8 flex flex-col items-center justify-center">
            <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            <p className="text-text-muted mt-3">Ищем адреса...</p>
          </div>
        )}

        {/* Search results */}
        {!isLoading && query && results.length > 0 && (
          <div className="px-4 pt-4">
            <p className="text-text-muted text-xs uppercase tracking-wider mb-3">
              {editMode ? `Выберите адрес для "${editMode === 'home' ? 'Дом' : 'Работа'}"` : 'Результаты поиска'}
            </p>
            {results.map((address, index) => (
              <AddressItem
                key={index}
                address={address}
                icon={<PlaceIcon />}
                onSelect={handleAddressSelect}
              />
            ))}
          </div>
        )}

        {/* No results */}
        {!isLoading && query && results.length === 0 && (
          <div className="py-12 flex flex-col items-center justify-center px-6">
            <div className="w-16 h-16 rounded-full bg-dark-tertiary flex items-center justify-center mb-4">
              <MapIcon />
            </div>
            <p className="text-text-primary font-medium text-center">Ничего не найдено</p>
            <p className="text-text-muted text-sm mt-1 text-center">Попробуйте изменить запрос или укажите адрес на карте</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Address list item
interface AddressItemProps {
  address: AddressResult
  icon: React.ReactNode
  onSelect: (address: AddressResult) => void
}

const AddressItem = ({ address, icon, onSelect }: AddressItemProps) => {
  const parts = address.display_name.split(',')
  const mainPart = parts[0]
  const secondaryPart = parts.slice(1, 3).join(',').trim()

  return (
    <button
      onClick={() => onSelect(address)}
      className="w-full flex items-center gap-4 py-3 hover:bg-primary/5 active:bg-primary/10 transition-colors rounded-lg -mx-2 px-2 text-left"
    >
      <div className="w-10 h-10 rounded-full bg-dark-tertiary flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-text-primary font-medium truncate">{mainPart}</p>
        {secondaryPart && (
          <p className="text-text-muted text-sm truncate">{secondaryPart}</p>
        )}
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted flex-shrink-0">
        <path d="M9 18l6-6-6-6"/>
      </svg>
    </button>
  )
}

export default AddressSearchScreen
